import LegalLayout from "./LegalLayout";
import { Sparkles, ShieldCheck, CheckCircle2, AlertTriangle, Scale } from "lucide-react";

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The terms and conditions governing your use of Virality AI, your intellectual property rights, and platform rules."
      lastUpdated="September 10, 2026"
    >
      <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">
        {/* Intro */}
        <div>
          <p className="text-base text-foreground font-medium">
            These Terms of Service (&ldquo;Terms&rdquo;) constitute a legally binding agreement between you (&ldquo;User&rdquo;, &ldquo;Creator&rdquo;, or &ldquo;you&rdquo;) and Virality AI (&ldquo;Virality&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) governing your access to and use of the Virality website, software, APIs, and services.
          </p>
          <p className="mt-2 text-xs">
            By creating an account, accessing, or using Virality, you signify that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
          </p>
        </div>

        {/* Ownership Highlight Box */}
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-accent shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display font-bold text-foreground text-sm">
                You Own 100% of Your Content
              </h4>
              <p className="mt-1 text-xs text-foreground/85">
                We believe creators must maintain full ownership of their art. Any script, hook, title, video outline, or copy you generate or refine using Virality belongs entirely to you. You are free to publish, monetize, copyright, or distribute your content anywhere in the world without paying royalties to Virality.
              </p>
            </div>
          </div>
        </div>

        {/* Section 1 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            1. Eligibility &amp; Account Registration
          </h2>
          <p>
            To use Virality, you must be at least 13 years of age (or the minimum legal age of digital consent in your jurisdiction). If you are using the service on behalf of an agency, company, or brand, you represent and warrant that you have authority to bind that entity to these Terms.
          </p>
          <p className="text-xs">
            You agree to provide accurate, complete registration details and maintain the security of your account credentials. You are responsible for all activities that occur under your account.
          </p>
        </section>

        {/* Section 2 */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            2. Description of the Service
          </h2>
          <p>
            Virality AI provides a content intelligence operating system designed for video and digital creators. The platform includes audience analysis, trend discovery, opportunity ranking, AI hook scoring, script generation, production workflow management, and authenticated social platform analytics.
          </p>
          <p className="text-xs">
            We continuously improve our features and algorithms. We reserve the right to modify, update, or discontinue features with reasonable notice where appropriate.
          </p>
        </section>

        {/* Section 3: Intellectual Property */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <Scale className="h-4 w-4 text-accent" /> 3. Intellectual Property Rights &amp; Content Ownership
          </h2>
          <div className="space-y-2.5 text-xs">
            <p>
              <strong>Creator Content:</strong> As between you and Virality, all scripts, text, video concepts, hooks, and materials created or input by you (&ldquo;User Content&rdquo;) remain your sole property.
            </p>
            <p>
              <strong>Limited License to Virality:</strong> You grant Virality a non-exclusive, worldwide, royalty-free license solely to transmit, host, display, and format your content as necessary to provide the service to you and your authorized team.
            </p>
            <p>
              <strong>Virality IP:</strong> The Virality platform, user interface, brand assets, algorithms, software code, logos, and proprietary scoring models are the exclusive intellectual property of Virality AI and its licensors.
            </p>
          </div>
        </section>

        {/* Section 4: Subscriptions & Billing */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            4. Subscriptions, Fees &amp; Cancellation
          </h2>
          <div className="space-y-2 text-xs">
            <p>
              <strong>Billing &amp; Renewals:</strong> Paid subscriptions are billed in advance on a recurring monthly or annual basis via our authorized payment processors (Stripe / LemonSqueezy). Your subscription automatically renews unless canceled before the billing cycle ends.
            </p>
            <p>
              <strong>Cancellation:</strong> You can cancel your subscription at any time directly through your <span className="font-semibold text-foreground">Settings &gt; Billing</span> page. Upon cancellation, you retain full access to your plan benefits until the end of your current paid billing period.
            </p>
            <p>
              <strong>Refunds:</strong> We provide refunds in accordance with our 14-day satisfaction guarantee for first-time subscribers upon request to <a href="mailto:getintouch@viralityai.media" className="text-accent underline">getintouch@viralityai.media</a>.
            </p>
          </div>
        </section>

        {/* Section 5: Third-Party Platforms */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            5. Third-Party Platforms &amp; API Compliance
          </h2>
          <p className="text-xs">
            Virality connects with third-party social platforms (including YouTube, Instagram, and TikTok). Your use of these platforms through our integration is subject to their respective terms and policies:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>By using YouTube features, you agree to be bound by the <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer" className="text-accent underline font-medium">YouTube Terms of Service</a>.</li>
            <li>By using Instagram features, you agree to comply with Meta&rsquo;s Platform Terms and Community Guidelines.</li>
            <li>Virality is an independent tool and is not officially affiliated with, endorsed by, or sponsored by Google, YouTube, Meta, or TikTok.</li>
          </ul>
        </section>

        {/* Section 6: Algorithm Disclaimer */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> 6. Disclaimer of Warranties (&ldquo;No Guaranteed Virality&rdquo;)
          </h2>
          <div className="rounded-xl border border-border bg-surface p-4 text-xs space-y-2">
            <p className="font-semibold text-foreground">
              Algorithmic Performance Disclaimer
            </p>
            <p>
              Virality provides algorithmic insights, trend telemetry, and creative suggestions based on historical data and audience resonance patterns. However, third-party social algorithms, audience tastes, and market conditions fluctuate constantly.
            </p>
            <p>
              <strong>THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo;.</strong> VIRALITY EXPRESSLY DISCLAIMS ANY WARRANTY OR GUARANTEE OF SPECIFIC VIEW COUNTS, ENGAGEMENT RATES, SUBSCRIBER INCREASES, OR MONETARY EARNINGS. YOU ASSUME FULL EDITORIAL AND STRATEGIC RESPONSIBILITY FOR YOUR PUBLISHED CONTENT.
            </p>
          </div>
        </section>

        {/* Section 7: Limitation of Liability */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            7. Limitation of Liability
          </h2>
          <p className="text-xs">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL VIRALITY AI, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR ACCESS TO OR USE OF THE SERVICE. OUR TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE TOTAL FEES PAID BY YOU TO VIRALITY IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        {/* Section 8: Termination */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            8. Termination
          </h2>
          <p className="text-xs">
            You may terminate your account at any time by requesting deletion in your settings or emailing support. Virality reserves the right to suspend or terminate accounts that violate our Acceptable Use Policy, engage in fraud, or abuse our infrastructure.
          </p>
        </section>

        {/* Section 9: Governing Law & Dispute Resolution */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            9. Governing Law
          </h2>
          <p className="text-xs">
            These Terms shall be governed by and construed in accordance with the laws of Delaware, United States, without regard to its conflict of law principles. Any dispute arising from these Terms shall be resolved through good-faith negotiation, followed by confidential binding arbitration if necessary.
          </p>
        </section>

        {/* Section 10: Contact */}
        <section className="space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground">
            10. Contact Information
          </h2>
          <div className="rounded-xl border border-border bg-surface p-4 text-xs">
            <p className="font-bold text-foreground">Virality Support</p>
            <p className="mt-1">Email: <a href="mailto:getintouch@viralityai.media" className="text-accent underline">getintouch@viralityai.media</a></p>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
}
